/**
 * Dashboard Router - Smart router that redirects users to their appropriate dashboard
 *
 * - Branch admins: Redirect to their branch dashboard
 * - Coaches: Redirect to their coach dashboard
 * - Parents: Show the parent dashboard (ParentDashboard)
 * - Players: Show the parent dashboard (ParentDashboard)
 * - Other roles: Access denied or redirect appropriately
 */

import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import LoadingState from '../components/LoadingState';
import ParentDashboard from './ParentDashboard';

const DashboardRouter = () => {
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { hasRole, isParent, isPlayer, userRole, userRoles } = useRole();

  // Debug logging
  console.log('DashboardRouter - Debug:', {
    user,
    authLoading,
    isAuthenticated,
    userRole,
    userRoles,
    isParent,
    isPlayer,
    hasRoleParent: hasRole('parent'),
    hasRolePlayer: hasRole('player')
  });

  // Show loading while checking authentication
  if (authLoading) {
    return <LoadingState />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // Club admins should use their club dashboard, not the parent personal dashboard
  if (hasRole('club_admin')) {
    return <Navigate to="/admin/club" replace />;
  }

  // Branch admins should use their branch dashboard, not the parent personal dashboard
  if (hasRole('branch_admin')) {
    const branch_id = user?.branch_id || user?.location_id;
    if (branch_id) {
      return <Navigate to={`/branch/${branch_id}/dashboard`} replace />;
    } else {
      // Branch admin without branch assignment - show error
      return (
        <div className="access-denied-page">
          <div className="access-denied-content">
            <div className="access-denied-icon">⚠️</div>
            <h2>Configuration Error</h2>
            <p>Your account is configured as a branch admin but is not associated with a branch.</p>
            <p>Please contact support for assistance.</p>
          </div>
        </div>
      );
    }
  }

  // Platform admins should use their platform dashboard
  if (hasRole('platform_admin')) {
    return <Navigate to="/admin/platform" replace />;
  }

  // Coaches should use their coach dashboard
  // Check both hasRole('coach') and roles array for backward compatibility
  console.log('🔍 Coach Detection Debug:', {
    hasRole_coach: hasRole('coach'),
    user_roles: user?.roles,
    user_roles_includes_coach: user?.roles?.includes('coach'),
    user_primary_role: user?.primary_role,
    userRoles_from_context: userRoles,
    userRoles_includes_coach: userRoles?.includes('coach')
  });
  
  const isCoach = hasRole('coach') || user?.roles?.includes('coach');
  
  console.log('🎯 DashboardRouter Coach Decision:', {
    isCoach: isCoach
  });
  
  if (isCoach) {
    console.log('✅ Redirecting coach to /coach/dashboard');
    return <Navigate to="/coach/dashboard" replace />;
  }

  // For parents ONLY, show the personal dashboard (ParentDashboard)
  if (hasRole('parent') || isParent) {
    return <ParentDashboard />;
  }

  // For players without parent role, show them the parent dashboard
  // TODO: Create a dedicated standalone player dashboard for players without parents
  if (hasRole('player') || isPlayer) {
    return <ParentDashboard />;
  }

  // FALLBACK: If user is authenticated but has no roles (backend issue),
  // check if they have account_type='parent' and show parent dashboard
  if (user && user.account_type === 'parent') {
    console.warn('DashboardRouter: User has no roles but account_type=parent, showing ParentDashboard as fallback');
    return <ParentDashboard />;
  }

  // FALLBACK 2: If user is authenticated and has a family_id, assume they're a parent
  if (user && user.family_id) {
    console.warn('DashboardRouter: User has no roles but has family_id, showing ParentDashboard as fallback');
    return <ParentDashboard />;
  }

  // For other roles, access denied
  return (
    <div className="access-denied-page">
      <div className="access-denied-content">
        <div className="access-denied-icon">🔒</div>
        <h2>Access Denied</h2>
        <p>The personal dashboard is only available for parents and players.</p>
        <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '1rem' }}>
          Debug: roles={JSON.stringify(userRoles)}, account_type={user?.account_type}, family_id={user?.family_id}
        </p>
        <button
          onClick={() => window.history.back()}
          className="back-button"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

export default DashboardRouter;
